import { useState } from "react";
import PageNavbar from "@/components/feature/PageNavbar";
import PageFooter from "@/components/feature/PageFooter";

const MIN_NAME_LENGTH = 3;
const MIN_MESSAGE_LENGTH = 20;
const SUPPORT_EMAIL = "support@inntellivex.com";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormValues = { name: string; email: string; subject: string; message: string };
type FieldErrors = Partial<Record<keyof FormValues, string>>;

const INITIAL_VALUES: FormValues = { name: "", email: "", subject: "", message: "" };

function getFieldErrors(values: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  if (values.name.trim().length > 0 && values.name.trim().length < MIN_NAME_LENGTH) {
    errors.name = `Name must be at least ${MIN_NAME_LENGTH} characters.`;
  }
  if (values.email.trim().length > 0 && !EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (values.message.trim().length > 0 && values.message.trim().length < MIN_MESSAGE_LENGTH) {
    errors.message = `Message must be longer than ${MIN_MESSAGE_LENGTH} characters.`;
  }
  return errors;
}

function isFormValid(values: FormValues): boolean {
  return (
    values.name.trim().length >= MIN_NAME_LENGTH &&
    EMAIL_PATTERN.test(values.email.trim()) &&
    values.subject.trim().length > 0 &&
    values.message.trim().length >= MIN_MESSAGE_LENGTH
  );
}

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
  const [touched, setTouched] = useState<Partial<Record<keyof FormValues, boolean>>>({});

  const fieldErrors = getFieldErrors(values);
  const formValid = isFormValid(values);

  const handleChange = (field: keyof FormValues) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleBlur = (field: keyof FormValues) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get("company_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setValues(INITIAL_VALUES);
      return;
    }

    setTouched({ name: true, email: true, subject: true, message: true });
    if (!formValid) {
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    const trimmedValues = {
      name: values.name.trim(),
      email: values.email.trim(),
      subject: values.subject.trim(),
      message: values.message.trim(),
    };

    const text = Object.entries(trimmedValues)
      .map(([key, value]) => `${key} - ${value}`)
      .join("\n");

    const payload = {
      email: trimmedValues.email,
      text,
      problemType: trimmedValues.subject,
      to_email: SUPPORT_EMAIL,
      subject: "Contact Form Submission",
    };

    try {
      const response = await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus("success");
        setErrorMsg("");
        form.reset();
        setValues(INITIAL_VALUES);
        setTouched({});
      } else {
        const responseText = await response.text();
        setStatus("error");
        setErrorMsg(responseText || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
      <PageNavbar />

      <section className="relative w-full overflow-hidden" style={{ padding: "80px 16px 50px" }}>
        <div className="mx-auto" style={{ maxWidth: 1440 }}>
          <div style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 16 }}>
            Get in touch
          </div>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 72px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#ECECEC", margin: 0, maxWidth: 900 }}>
            Let's build something great together.
          </h1>
        </div>
      </section>

      <div className="mx-auto flex flex-col lg:grid gap-10 lg:gap-16" style={{ maxWidth: 1440, padding: "0 16px", marginBottom: 80, gridTemplateColumns: "1.2fr 1fr" }}>
        {/* Form */}
        <div>
          <form
            onSubmit={handleSubmit}
            style={{ position: "relative", display: "flex", flexDirection: "column", gap: 20 }}
          >
            <div>
              <label style={{ display: "block", fontSize: 13, color: "#8A8A8A", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Name</label>
              <input
                type="text"
                name="name"
                required
                minLength={MIN_NAME_LENGTH}
                value={values.name}
                onChange={handleChange("name")}
                onBlur={handleBlur("name")}
                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${touched.name && fieldErrors.name ? "#EF4444" : "rgba(255,255,255,0.12)"}`, borderRadius: 12, padding: "14px 16px", color: "#ECECEC", fontSize: 15, outline: "none" }}
              />
              {touched.name && fieldErrors.name && (
                <div style={{ color: "#EF4444", fontSize: 12, marginTop: 6 }}>{fieldErrors.name}</div>
              )}
            </div>
            <div>
              <label style={{ display: "block", fontSize: 13, color: "#8A8A8A", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Email</label>
              <input
                type="email"
                name="email"
                required
                value={values.email}
                onChange={handleChange("email")}
                onBlur={handleBlur("email")}
                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${touched.email && fieldErrors.email ? "#EF4444" : "rgba(255,255,255,0.12)"}`, borderRadius: 12, padding: "14px 16px", color: "#ECECEC", fontSize: 15, outline: "none" }}
              />
              {touched.email && fieldErrors.email && (
                <div style={{ color: "#EF4444", fontSize: 12, marginTop: 6 }}>{fieldErrors.email}</div>
              )}
            </div>
            <div>
              <label style={{ display: "block", fontSize: 13, color: "#8A8A8A", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Subject</label>
              <input
                type="text"
                name="subject"
                required
                value={values.subject}
                onChange={handleChange("subject")}
                onBlur={handleBlur("subject")}
                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${touched.subject && !values.subject.trim() ? "#EF4444" : "rgba(255,255,255,0.12)"}`, borderRadius: 12, padding: "14px 16px", color: "#ECECEC", fontSize: 15, outline: "none" }}
              />
              {touched.subject && !values.subject.trim() && (
                <div style={{ color: "#EF4444", fontSize: 12, marginTop: 6 }}>Subject is required.</div>
              )}
            </div>
            <div>
              <label style={{ display: "block", fontSize: 13, color: "#8A8A8A", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Message</label>
              <textarea
                name="message"
                required
                minLength={MIN_MESSAGE_LENGTH}
                maxLength={500}
                value={values.message}
                onChange={handleChange("message")}
                onBlur={handleBlur("message")}
                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${touched.message && fieldErrors.message ? "#EF4444" : "rgba(255,255,255,0.12)"}`, borderRadius: 12, padding: "14px 16px", color: "#ECECEC", fontSize: 15, outline: "none", minHeight: 160, resize: "vertical", fontFamily: "inherit" }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                {touched.message && fieldErrors.message ? (
                  <div style={{ color: "#EF4444", fontSize: 12 }}>{fieldErrors.message}</div>
                ) : (
                  <span />
                )}
                <div style={{ fontSize: 12, color: "#8A8A8A" }}>
                  {values.message.length} / 500
                </div>
              </div>
            </div>
            {/* Honeypot */}
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />
            <button
              type="submit"
              disabled={status === "loading" || !formValid}
              className="whitespace-nowrap cursor-pointer"
              style={{
                background: status === "success" ? "#10B981" : "#2563EB",
                color: "#fff",
                border: "none",
                borderRadius: 9999,
                padding: "14px 32px",
                fontWeight: 600,
                fontSize: 15,
                alignSelf: "flex-start",
                opacity: status === "loading" || !formValid ? 0.5 : 1,
                cursor: !formValid ? "not-allowed" : "pointer",
                transition: "background 0.3s ease, opacity 0.3s ease",
              }}
            >
              {status === "loading" ? "Sending..." : status === "success" ? "Message sent!" : "Send Message"}
            </button>
            {status === "error" && (
              <div style={{ color: "#EF4444", fontSize: 14 }}>{errorMsg}</div>
            )}
            {status === "success" && (
              <div style={{ color: "#10B981", fontSize: 14 }}>Thank you! We'll get back to you shortly.</div>
            )}
          </form>
          <style>{`
            .hp-field { position: absolute; left: -9999px; opacity: 0; }
          `}</style>
        </div>

        {/* Contact info */}
        <div style={{ paddingLeft: 0 }}>
          <div style={{ marginBottom: 40 }}>
            <div style={{ fontSize: 13, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12 }}>Address</div>
            <div style={{ fontSize: 16, color: "#ECECEC", lineHeight: 1.6 }}>
              50 Essex Street<br />London, WC2R 3JF<br />United Kingdom
            </div>
          </div>
          <div style={{ marginBottom: 40 }}>
            <div style={{ fontSize: 13, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12 }}>Email</div>
            <div style={{ fontSize: 16, color: "#ECECEC", lineHeight: 1.6 }}>
              support@inntellivex.com
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12 }}>Phone</div>
            <div style={{ fontSize: 16, color: "#ECECEC", lineHeight: 1.6 }}>
              +447520680012
            </div>
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  );
}